// §11.10 — the calendar's arithmetic.
//
// It sits in `shared` rather than beside the app because §11.10 asks for the same month in two
// places — the Clock & Calendar window and the skin's tray flyout — and §6.5 lets an app and a
// skin share exactly one thing: this package. Pure functions, no dependencies, like `vfs.ts`.
//
// Everything here is **local time**, deliberately: a notification that arrived at 11 pm belongs on
// the day the person reading it remembers, and that is their own calendar, not UTC's.

export interface CalendarDay {
  readonly year: number;
  /** 0-based, as `Date` counts them. */
  readonly month: number;
  readonly day: number;
  /** `YYYY-MM-DD` — the key notifications are bucketed under. */
  readonly key: string;
  /** False for the leading and trailing days that belong to the neighbouring months. */
  readonly inMonth: boolean;
}

const pad = (value: number): string => String(value).padStart(2, '0');

export function dayKeyOf(year: number, month: number, day: number): string {
  return `${year}-${pad(month + 1)}-${pad(day)}`;
}

/** The day a timestamp falls on, in the reader's own timezone. */
export function dayKeyAt(at: number): string {
  const date = new Date(at);
  return dayKeyOf(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Six weeks of cells, always — a grid that changed height between March and April would move
 * everything under it, and the month with 31 days starting on a Saturday genuinely needs six.
 */
export function monthGrid(year: number, month: number): CalendarDay[] {
  const first = new Date(year, month, 1);
  const start = new Date(year, month, 1 - first.getDay());
  const days: CalendarDay[] = [];
  for (let index = 0; index < 42; index += 1) {
    const date = new Date(start.getFullYear(), start.getMonth(), start.getDate() + index);
    days.push({
      year: date.getFullYear(),
      month: date.getMonth(),
      day: date.getDate(),
      key: dayKeyOf(date.getFullYear(), date.getMonth(), date.getDate()),
      inMonth: date.getMonth() === month,
    });
  }
  return days;
}

export function addMonths(year: number, month: number, delta: number): { year: number; month: number } {
  const total = year * 12 + month + delta;
  return { year: Math.floor(total / 12), month: ((total % 12) + 12) % 12 };
}

/** Bucket anything with an `at` by the day it happened on. */
export function byDay<T extends { readonly at: number }>(
  items: readonly T[],
): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const item of items) {
    const key = dayKeyAt(item.at);
    const list = out.get(key);
    if (list) list.push(item);
    else out.set(key, [item]);
  }
  return out;
}
